import { type QueryKey, type QueryObserverOptions, useQuery } from '@tanstack/react-query';
import { receivableInvoicesApiNamespace } from 'receivable-invoices-js/constants/hosts';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import { type ClientPortalSettingsAPI } from 'receivable-invoices-js/react/api/models/client-portal-settings';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';

interface Params {
  clientId: string;
}

interface QueryResponse {
  data: ClientPortalSettingsAPI;
}

interface CustomOptions {
  onError?: (error: unknown) => void;
}

const URL = `${receivableInvoicesApiNamespace}/receivable_invoices/client_portals/shared`;

export function fetchClientPortalSettingsKey(clientId: string): QueryKey {
  return ['client-portal-settings', clientId] as QueryKey;
}

export async function fetchClientPortalSettings(params: Params, customOptions: CustomOptions) {
  const { onError } = customOptions;

  try {
    const response = await qontoClient<QueryResponse>(`${URL}/${params.clientId}/settings`);

    const normalizedResponse = {
      ...response.data.attributes,
      id: response.data.id,
    };

    return camelizeKeys(normalizedResponse);
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
}

export type FetchClientPortalSettingsResponse = Awaited<
  ReturnType<typeof fetchClientPortalSettings>
>;

export function useFetchClientPortalSettings(
  params: Params,
  customOptions?: CustomOptions,
  queryOptions?: Partial<QueryObserverOptions<FetchClientPortalSettingsResponse>>
) {
  return useQuery({
    queryKey: fetchClientPortalSettingsKey(params.clientId),
    queryFn: () => fetchClientPortalSettings(params, { ...customOptions }),
    ...queryOptions,
  });
}
